import {Fragment, useEffect, useState} from 'react';
import './activities-group-entry-configuration-value.scss';
import {Chip, Stack, Tooltip, Typography} from '@mui/material';
import {CarePlanConfiguration} from '../../../backend/interfaces/care-plan-configuration';
import {CarePlanConfigurationType} from '../../../backend/enums/care-plan-configuration-type';
import {CarePlanConfigurationFeature} from '../../../backend/enums/care-plan-configuration-feature';
import {convertArrayToIndex} from '../../../utils/functions/convert-array-to-index';
import {Pallete} from '../../../theme/bluebird';
import {getDaysOfTheWeek} from '../../../utils/functions/days-of-the-week';
import {ActivitiesGroupSchedule, ActivityTypeSchedule} from '../../../types';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';

type TemporaryConfigurationOverride = Omit<CarePlanConfiguration, 'type'> & {
  type: CarePlanConfigurationType | CarePlanConfigurationFeature;
};

interface CarePlanSheetActivitiesGroupEntryConfigurationValueProps {
  configuration: TemporaryConfigurationOverride;
  value: string | string[];
  onEditMode: boolean;
  renderArrayTypeAsText?: boolean;
}

const RepetitionField = ({configurationId, value}: {configurationId?: string; value: string}) => {
  const {t} = useTranslation();

  let JSONValue: ActivitiesGroupSchedule | ActivityTypeSchedule;

  // FIX-ME: Here as prevention of crashing when a non-parsable JSON ie. old data.
  try {
    JSONValue = JSON.parse(value);
  } catch (e) {
    return <Fragment key={configurationId ?? value} />;
  }

  // @ts-ignore
  if (!JSONValue.type) return <Fragment key={configurationId ?? value} />;

  // @ts-ignore
  const {type, frequency, days} = JSONValue;

  const weekDays = getDaysOfTheWeek();

  return (
    <Stack flexDirection="column" sx={{width: '100%', gap: '5px', justifyContent: 'space-between'}}>
      <span className="value">
        {type === 'activities-group'
          ? t(
              translations.care_plan_sheet_activities_group_entry_configurationvalue.repetition
                .per_activity
            )
          : t(
              translations.care_plan_sheet_activities_group_entry_configurationvalue.repetition
                .per_task
            )}
      </span>
      <Stack flexDirection="row" sx={{width: '100%', gap: '4.5px'}}>
        {type === 'activities-group' &&
          frequency === 'weekly' &&
          weekDays.map((day, index) => {
            // @ts-ignore
            const selected = days.includes(day.key) ?? false;

            return (
              <Tooltip key={index} title={day.label}>
                <Chip
                  sx={{
                    backgroundColor: !selected ? Pallete.gray.lightbg : Pallete.blue.lighter,
                    color: !selected ? Pallete.gray.light : Pallete.blue.base,
                    fontSize: '12px',
                    fontWeight: 500,
                    height: '20px',
                    width: '20px',
                    '& .MuiChip-label': {
                      display: 'block',
                      padding: '0px 2px',
                      whiteSpace: 'normal',
                    },
                  }}
                  label={day.label.substring(0, 1)}
                  variant="filled"
                />
              </Tooltip>
            );
          })}
      </Stack>
    </Stack>
  );
};

interface ArrayFieldProps {
  configuration: TemporaryConfigurationOverride;
  value: string | string[];
  onEditMode: boolean;
  renderAsText?: boolean;
}

export const ArrayField = ({configuration, value, onEditMode, renderAsText}: ArrayFieldProps) => {
  const [arrayItemsIndex, setArrayItemsIndex] = useState<any>(undefined);

  // On mount
  useEffect(() => {
    // Set values index
    setArrayItemsIndex(convertArrayToIndex(configuration?.arrayItems ?? [], 'id'));
  }, [configuration]);

  return (
    <>
      {/* renders the value as plain text when we have an array with multipleValues set to false */}
      {configuration.type === CarePlanConfigurationType.Array &&
        !configuration.multipleValues &&
        arrayItemsIndex &&
        value && (
          <div className="value">
            {/** look for matches with value as a sole id or id[] */}
            {arrayItemsIndex[value as string]?.name || arrayItemsIndex[value[0]].name}
          </div>
        )}

      {/* also renders the multiple values as plain text if forced to with the renderAsText prop */}
      {configuration.type === CarePlanConfigurationType.Array &&
        configuration.multipleValues &&
        arrayItemsIndex &&
        Array.isArray(value) &&
        renderAsText && (
          <div className="value">{value.map((item) => arrayItemsIndex[item].name).join(', ')}</div>
        )}

      {/* otherwise render each array value as a chip */}
      {configuration.type === CarePlanConfigurationType.Array &&
        configuration.multipleValues &&
        !renderAsText && (
          <Stack className="value" gap="3px" flexDirection="row" flexWrap="wrap">
            {arrayItemsIndex &&
              Array.isArray(value) &&
              value.map((item: string, index: number) => (
                <Tooltip
                  key={`tooltip_item_${index}`}
                  title={arrayItemsIndex ? (arrayItemsIndex[item]?.description ?? '') : ''}
                >
                  <Chip
                    sx={{
                      backgroundColor: onEditMode ? Pallete.gray.lightbg : Pallete.blue.lighter,
                      color: onEditMode ? Pallete.gray.light : Pallete.blue.base,
                      borderRadius: '24px',
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: '18px',
                      padding: '0px 6px',
                      height: 'auto',
                      width: 'auto',
                      '& .MuiChip-label': {
                        display: 'block',
                        padding: '0px 2px',
                        whiteSpace: 'normal',
                      },
                    }}
                    label={arrayItemsIndex[item].name}
                    variant="filled"
                  />
                </Tooltip>
              ))}
          </Stack>
        )}
    </>
  );
};

export const CarePlanSheetActivitiesGroupEntryConfigurationValue = ({
  configuration,
  value,
  onEditMode,
  renderArrayTypeAsText,
}: CarePlanSheetActivitiesGroupEntryConfigurationValueProps) => {
  // Translations
  const {t} = useTranslation();

  // FIX-ME: Fail safe
  if (!configuration) return null;

  return (
    <>
      <Tooltip title={configuration?.description}>
        <label className="title">{configuration?.name}</label>
      </Tooltip>

      {!value || (Array.isArray(value) && value.length === 0) ? (
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.25px',
            color: '#B6B6B6',
          }}
        >
          {t(translations.activities_group_entry.configuration_type_not_defined)}
        </Typography>
      ) : (
        <>
          {configuration.type === CarePlanConfigurationType.String && (
            <div className="value">{value}</div>
          )}

          {configuration.type === CarePlanConfigurationFeature.Schedule && (
            <div className="value">{value}</div>
          )}

          {configuration.type === CarePlanConfigurationType.ActivitiesGroupSchedule &&
            typeof value === 'string' && (
              <RepetitionField configurationId={configuration.id} value={value} />
            )}

          {configuration.type === CarePlanConfigurationType.ActivityTypeSchedule && (
            <div className="value">
              {t(translations.activities_group_entry.configuration_type_label)}: {value}
            </div>
          )}

          {configuration.type === CarePlanConfigurationType.Array && (
            <ArrayField
              {...{configuration, value, onEditMode, renderAsText: renderArrayTypeAsText}}
            />
          )}
        </>
      )}
    </>
  );
};
